import { useState, useRef, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, CircularProgress, Divider, Stack, Tooltip, Typography, useTheme } from "@mui/material";

// Import necessary styles for annotations and text layers
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { ChatButton } from "Common/Components";
import { KeyboardArrowDown, KeyboardArrowUp, WarningAmber, ZoomInOutlined, ZoomOutOutlined } from "@mui/icons-material";

// Set the worker from the CDN - must match the pdfjs-dist version bundled by react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs`;

interface IPdfViewerProps {
  url: string | undefined;
  initialPage: number;
  pageResetKey: number | undefined;
  warningMessage?: string;
}

export const PdfViewer = (props: IPdfViewerProps) => {
  const { url, initialPage, pageResetKey, warningMessage } = props;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState<number>(1); // Zoom level, default 1.0
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);

  // Reference for the container div
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert blob URL to Uint8Array to avoid pdfjs worker transfer/detach issues with blob: URLs
  useEffect(() => {
    if (url) {
      fetch(url)
        .then((res) => res.arrayBuffer())
        .then((data) => setPdfData(new Uint8Array(data)))
        .catch((err) => {
          console.error("Failed to load PDF data from blob URL:", err);
          setError("Failed to load PDF");
        });
    } else {
      setPdfData(null);
    }
  }, [url]);

  // Memoize the file object to prevent react-pdf from reloading on every render
  const pdfFile = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

  useEffect(() => {
    if (initialPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage, pageResetKey]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setIsPdfLoading(false);
    setError(null); // Clear any previous errors
  }

  function onDocumentLoadError(error: Error): void {
    setError("Failed to load PDF");
    setIsPdfLoading(false);
    console.error("Error while loading PDF:", error);
  }

  // Zoom In functionality
  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 2.0)); // Maximum scale is 2.0
  };

  // Zoom Out functionality
  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5)); // Minimum scale is 0.5
  };

  const handleNextPage = () => {
    if (numPages !== null && currentPage < numPages) {
      setCurrentPage((prevCurrentPage) => prevCurrentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevCurrentPage) => prevCurrentPage - 1);
    }
  };

  const handleOnLoadStart = () => {
    setIsPdfLoading(true);
  };

  const theme = useTheme();

  return (
    <Stack
      sx={{
        display: "flex",
        alignItems: "start",
        justifyContent: "space-between",
        background: theme.palette.grey[100],
        height: "100%",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          alignItems: "start",
          display: "flex",
          flexDirection: "column",
          maxWidth: "50px",
          gap: 2,
          p: 1,
        }}
      >
        <Stack sx={{ alignItems: "center", display: "flex", flexDirection: "row", gap: 1 }}>
          <ChatButton
            tooltipTitle="Zoom In"
            hasBackground
            backgroundColor={theme.palette.grey[300]}
            Icon={ZoomInOutlined}
            onClick={handleZoomIn}
          />
        </Stack>
        <Tooltip title="Scale" placement="right-start" arrow>
          <Typography variant="body1">{Math.round(scale * 100)}%</Typography>
        </Tooltip>
        <Stack sx={{ alignItems: "center", display: "flex", flexDirection: "row", gap: 1 }}>
          <ChatButton
            tooltipTitle="Zoom Out"
            hasBackground
            backgroundColor={theme.palette.grey[300]}
            Icon={ZoomOutOutlined}
            onClick={handleZoomOut}
          />
        </Stack>

        <Stack
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <ChatButton
            tooltipTitle="Previous Page"
            hasBackground
            backgroundColor={theme.palette.grey[300]}
            Icon={KeyboardArrowUp}
            onClick={handlePreviousPage}
          />
          <Tooltip title="Page Number" placement="right-start" arrow>
            <Typography variant="body1">{currentPage}</Typography>
          </Tooltip>
          <Divider flexItem sx={{ marginY: 1 }} />
          <Tooltip title="Total Pages" placement="right-start" arrow>
            <Typography variant="body1">{numPages}</Typography>
          </Tooltip>

          <ChatButton
            tooltipTitle="Next Page"
            hasBackground
            backgroundColor={theme.palette.grey[300]}
            Icon={KeyboardArrowDown}
            onClick={handleNextPage}
          />
        </Stack>
      </Box>
      <Box
        sx={{
          borderLeft: `1px solid ${theme.palette.grey[300]}`,
          height: "100%",
          width: "100%",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "start",
          position: "relative",
        }}
      >
        {isPdfLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Box
          ref={containerRef}
          style={{
            flexGrow: 1,
            overflow: "auto",
            boxSizing: "border-box",
            height: "100%",
          }}
        >
          {warningMessage ? (
            <Box
              sx={{
                background: "#fff1dc",
                border: `1px solid #FBD288`,
                display: "flex",
                padding: 0,
                py: 0.5,
                textAlign: "center",
                justifyContent: "center",
                color: theme.palette.warning.dark,
              }}
            >
              <WarningAmber />
              <Typography sx={{ px: 1, fontSize: "12px", fontWeight: 600 }}>{warningMessage}</Typography>
            </Box>
          ) : null}

          {pdfFile ? (
            <Box
              sx={{
                overflow: "auto",
                maxHeight: "calc(100vh - 120px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Document
                file={pdfFile}
                onLoadStart={handleOnLoadStart}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
              >
                <Page pageNumber={currentPage} scale={scale} />
              </Document>
              {error && <span>{error}</span>}
            </Box>
          ) : (
            <p>No PDF available to display.</p>
          )}
        </Box>
      </Box>
    </Stack>
  );
};
